// src/components/FullScreenCamera.js
// Full screen camera takeover — instant, no friction
import React, { useRef, useEffect, useState } from 'react'

export default function FullScreenCamera({ onCapture, onClose, cityColor = '#EE352E' }) {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [facingMode, setFacingMode] = useState('environment')
  const [ready, setReady] = useState(false)
  const [capturing, setCapturing] = useState(false)

  const startCamera = async (mode) => {
    // Stop existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
        setReady(true)
      }
    } catch(e) {
      console.error('Camera error:', e)
      // Fallback to file picker
      onClose('picker')
    }
  }

  useEffect(() => {
    startCamera(facingMode)
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop())
    }
  }, []) // eslint-disable-line

  const flipCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(next)
    startCamera(next)
  }

  const capture = () => {
    if (!videoRef.current || !ready) return
    setCapturing(true)
    const canvas = document.createElement('canvas')
    const v = videoRef.current
    // Square crop from center
    const size = Math.min(v.videoWidth, v.videoHeight)
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.drawImage(v, (v.videoWidth - size) / 2, (v.videoHeight - size) / 2, size, size, 0, 0, size, size)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88)
    setTimeout(() => {
      setCapturing(false)
      onCapture(dataUrl)
    }, 150)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999999,
      background: '#000',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Live viewfinder */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      />

      {/* Flash on capture */}
      {capturing && (
        <div style={{ position: 'absolute', inset: 0, background: '#fff', zIndex: 2, opacity: 0.7 }} />
      )}

      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        padding: '52px 20px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
        zIndex: 3,
      }}>
        <button onClick={() => onClose()} style={{
          background: 'rgba(0,0,0,0.4)', border: 'none',
          borderRadius: '50%', width: 40, height: 40,
          color: '#fff', fontSize: 18, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>

        <button onClick={flipCamera} style={{
          background: 'rgba(0,0,0,0.4)', border: 'none',
          borderRadius: '50%', width: 40, height: 40,
          color: '#fff', fontSize: 16, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>⟳</button>
      </div>

      {/* Bottom — label + shutter */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '20px 20px 52px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
        zIndex: 3,
      }}>
        <div style={{
          fontSize: 10, letterSpacing: 4, color: 'rgba(255,255,255,0.7)',
          fontWeight: 700, fontFamily: 'Helvetica Neue, Arial, sans-serif',
        }}>CAPTURE A MOMENT</div>

        {/* Shutter button */}
        <button
          onClick={capture}
          disabled={!ready}
          style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#fff', border: `4px solid rgba(255,255,255,0.4)`,
            cursor: 'pointer', outline: 'none',
            boxShadow: `0 0 0 3px ${cityColor}`,
            transition: 'transform 0.1s',
            transform: capturing ? 'scale(0.9)' : 'scale(1)',
          }}
        />
      </div>

      {/* Not ready overlay */}
      {!ready && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#000',
        }}>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, letterSpacing: 2 }}>
            OPENING CAMERA…
          </div>
        </div>
      )}
    </div>
  )
}
